import { Component, Inject, OnInit, OnDestroy, Optional } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { PublicSettingsService } from '../../../_services/app-settings/public-settings.service';
import { SESSION_DATE_PICKER_FORWARD_DAYS } from '../../../_constants/session-rules';
import { PlacementData } from '../../../_models';
import { PlacementService, SessionService } from '../../../_services';
import { CustomValidators } from '../../../_helpers/custom-validators';
import { TutorReportsHelperService } from '../../../_helpers/tutor-reports-helper/tutor-reports-helper.service';

import { PlacementSession } from '../../../_models/tutor-reports/placement-session.model';
import { ErrorModalHelperService } from '../../../_helpers/error-modal-helper/error-modal-helper.service';
import { SuccessModalHelperService } from '../../../_helpers/success-modal-helper/success-modal-helper.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

export class DialogData {
  startDate: Date;
  startTime: any;
  endTime: any;
  timeLocked: boolean;
  method: string;
  completed: boolean;
  status: string;
}

@Component({
    selector: 'session-date-picker-modal',
    templateUrl: './session-date-picker-modal.component.html',
    styleUrls: ['./session-date-picker-modal.component.css'],
    standalone: false
})
export class SessionDatePickerModalComponent implements OnInit, OnDestroy {

  public placementData: PlacementData;
  private unsubscribe: Subject<void> = new Subject<void>();

  public frmSessionDatePicker: UntypedFormGroup;

  public disableConfirm: boolean = false;

  public sessionTimeError: string = "";
  public errorText: string = "";
  public startDateErrorMessage: string = "";
  public selectedDay: string = "";
  public sessionDuration: string = "";

  public maxDateLessThanMinDate: boolean = false;

  private minDate: Date = new Date(new Date().setDate(new Date().getDate() - 1));
  private maxDate: Date = new Date(new Date().setDate(new Date().getDate() + 1));

  /**
   * The latest date the picker allows, in days from today (never past the booking's end): the served
   * sessions.date-picker-forward-days, SESSION_DATE_PICKER_FORWARD_DAYS (7) until it arrives or if it fails.
   */
  public datePickerForwardDays: number = SESSION_DATE_PICKER_FORWARD_DAYS;

  private model: any;
  private weekday: string[] = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"];

  private sessionBlockHolder: Array<PlacementSession> = new Array<PlacementSession>();
  public sessionBlocks: Array<PlacementSession> = new Array<PlacementSession>();
  public sessionBlockPageNumber: number = 1;
  public sessionBlockDisplayEndIndex: number = 0;
  public lastSessionBlockPage: boolean = false;
  public showMore: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dialogRef: MatDialogRef<SessionDatePickerModalComponent>,
    private formBuilder: UntypedFormBuilder,
    public tutorReportsHelperService: TutorReportsHelperService,
    private placementService: PlacementService,
    private sessionService: SessionService,
    private successModalHelper: SuccessModalHelperService,
    private errorModalHelper: ErrorModalHelperService,
    @Optional() private publicSettings: PublicSettingsService = null
  ) {
    // Widen the panel for this modal only, regardless of which call site opened it. Scoped via a
    // dedicated overlay class so the shared 'my-dialog' panel class is left untouched.
    this.dialogRef.addPanelClass('session-date-picker-dialog');
  }

  ngOnInit() {
    this.tutorReportsHelperService.placementData$
      .pipe(
        filter((d): d is PlacementData => d != null),
        takeUntil(this.unsubscribe)
      )
      .subscribe(pd => this.placementData = pd);

    // A cached value arrives synchronously, before the form is built; a later one re-applies the limit.
    let formBuilt = false;
    this.publicSettings?.sessionDatePickerForwardDays().pipe(take(1), takeUntil(this.unsubscribe)).subscribe(days => {
      this.datePickerForwardDays = days;
      if (formBuilt) {
        this.applyDateLimits();
        this.frmSessionDatePicker.get('startDate').setValidators(Validators.compose([
          Validators.required,
          CustomValidators.DateRangeValidator('startDate', this.minDate, this.maxDate)
        ]));
        this.frmSessionDatePicker.get('startDate').updateValueAndValidity();
      }
    });

    this.createForm();
    formBuilt = true;
    if (this.data && !this.data.timeLocked) {
      this.model = this.tutorReportsHelperService.studentSession;
      if (this.data.method != "Add") {
        this.model.startDate = this.data.startDate;
        this.model.startTime = this.data.startTime;
        this.model.endTime = this.data.endTime;
      }
      this.populateForm(this.model);
    } else {
      this.populateTimeLockedSessionBlocks();
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public getExternalSession(): void {
    this.errorText = '';
    var formModel = this.frmSessionDatePicker.value;
    var data1 = {
      startDate: new Date(this.setDate(formModel.startDate)),
      placementId: this.tutorReportsHelperService.commonData.placementId
    }

    this.frmSessionDatePicker.enable();

    this.placementService.getStudentSessionExternalData(data1).subscribe((data: any) => {

      this.applyDateLimits();

      this.frmSessionDatePicker.get('startDate').setValidators(Validators.compose([
        Validators.required,
        CustomValidators.DateRangeValidator('startDate', this.minDate, this.maxDate)
      ]));
      this.frmSessionDatePicker.get('startTime').setValidators(Validators.compose([
        Validators.required,
      ]));
      this.frmSessionDatePicker.get('endTime').setValidators(Validators.compose([
        Validators.required
      ]));

      if (data !== null) {
        data.startDate = formModel.startDate;
        this.populateTimesheet(data);
      }
      else {
        this.resetForm();

        if (this.placementData.aPorSP === 'AP') {
          this.frmSessionDatePicker.get('startDate').setValidators(Validators.compose([
            Validators.required,
            this.dateSessionError(true, { noSessionAvailable: true }),
            CustomValidators.DateRangeValidator('startDate', this.minDate, this.maxDate)
          ]));
          this.startDateErrorMessage = "There is no session available on the selected date";
        }
      }

      this.setTimeValidation();

      this.frmSessionDatePicker.get('startDate').updateValueAndValidity();
      this.frmSessionDatePicker.updateValueAndValidity();

      this.setDisplaySelectedDay(this.frmSessionDatePicker.get('startDate').value);
      this.updateDurationDisplay();

    });
  }

  public closeDialog(completed: boolean): void {
    let result = new DialogData();
    if (completed) {
      var formModel = this.frmSessionDatePicker.value;
      formModel.startDate = new Date(this.setDate(formModel.startDate));
      formModel.startDate.setHours(12, 0, 0 ,0);
      result.completed = true;

      result.endTime = formModel.endTime;
      result.startTime = formModel.startTime;
      result.startDate = formModel.startDate;
      result.status = formModel.status;
    } else {
      result.completed = false;
    }

    this.dialogRef.close(result);
  }

  public updateDurationDisplay(triggeredBy: string = 'code'): void {
    if (triggeredBy != "code") {
      this.setTimeValidation();
    }

    let startTimeDate = new Date("01/01/0001 " + this.frmSessionDatePicker.get("startTime").value);
    let endTimeDate = new Date("01/01/0001 " + this.frmSessionDatePicker.get("endTime").value);

    let diffInMinutes = (Math.abs(startTimeDate.getTime() - endTimeDate.getTime()) / 1000) / 60;
    let sessionDurationHours = Math.floor(diffInMinutes / 60);
    let sessionDurationMin = diffInMinutes % 60;
    this.sessionDuration = sessionDurationHours + " Hour(s) " + sessionDurationMin + " Min";

  }

  public clearResetDates(): void {
    if (this.data.method == "Add") {
      this.frmSessionDatePicker.reset();
      this.sessionBlocks.forEach((item: PlacementSession, i: number) => {
        item.selected = false;
      });
      this.sessionBlockHolder.forEach((item: PlacementSession, i: number) => {
        item.selected = false;
      });
    } else {
      if (this.data.timeLocked) {
        this.displayCurrentSelectedBlock();
      } else {
        this.populateForm(this.data);
      }
    }
  }

  public setDisplaySessionBlockDay(date: any): string {
    return this.weekday[new Date(date).getDay()];
  }

  public triggerShowMore(): void {
    this.showMore = !this.showMore;
    let sliceIndex = 0;
    if (this.sessionBlockHolder.some((item: PlacementSession) => item.selected)) {
      sliceIndex = this.sessionBlockHolder.findIndex((item: PlacementSession) => item.selected);
    } else {
      sliceIndex = this.sessionBlockHolder.findIndex((item: PlacementSession) => item.suggestedSession);
    }
    
    if (sliceIndex != -1) {
      let sliceStart = sliceIndex + 1;

      if (sliceIndex < (this.showMore ? 16 : 3)) {
        this.sessionBlockPageNumber = 1
      } else {
        this.sessionBlockPageNumber = Math.floor(sliceStart/(this.showMore ? 16 : 3));
        if (sliceStart % (this.showMore ? 16 : 3) != 0) {
          this.sessionBlockPageNumber ++;
        }
      }
    }
    this.setSessionBlockDisplayArray();
  }

  public sessionBlockChange(direction: string): void {
    if (direction == "next")
      this.sessionBlockPageNumber ++;
    else if (direction == "previous")
    this.sessionBlockPageNumber --;

    this.setSessionBlockDisplayArray();
  }

  public selectSessionBlock(sessionBlock: PlacementSession, index: number): void {
    
    if (!sessionBlock.disabled) {
      
      let itemIndex = this.sessionBlockHolder.findIndex(item => item.startDate == sessionBlock.startDate && item.startTime == sessionBlock.startTime && item.endTime == sessionBlock.endTime);
      this.sessionBlockHolder.forEach((item: PlacementSession, i: number) => {
        if (i == itemIndex) {
          item.selected = true;
          this.frmSessionDatePicker.get("startDate").setValue(item.startDate);
          this.frmSessionDatePicker.get("endTime").setValue(item.endTime);
          this.frmSessionDatePicker.get("startTime").setValue(item.startTime);
          this.frmSessionDatePicker.get("status").setValue(item.status);
          this.frmSessionDatePicker.updateValueAndValidity();
        } else {
          item.selected = false;
        }
      });
      this.setSessionBlockDisplayArray();
    }
  }

  private setSessionBlockDisplayArray(): void {
    this.sessionBlockDisplayEndIndex = this.sessionBlockPageNumber == 1 ? (this.showMore ? 16 : 3) : this.sessionBlockPageNumber * (this.showMore ? 16 : 3);
    this.sessionBlocks = this.sessionBlockHolder.slice(this.sessionBlockDisplayEndIndex - (this.showMore ? 16 : 3), this.sessionBlockDisplayEndIndex);
    this.lastSessionBlockPage = this.sessionBlockHolder.length <= this.sessionBlockDisplayEndIndex;
  }

  private resetForm(): void {
    this.frmSessionDatePicker.reset();
    this.errorText = '';
  }

  /**
   * The picker's range: the booking's start to its end, but never later than datePickerForwardDays from
   * today.
   */
  private applyDateLimits(): void {
    this.minDate = new Date(this.tutorReportsHelperService.commonData.placementStartDate);
    this.maxDate = new Date(this.tutorReportsHelperService.commonData.placementEndDate);

    let maxDateDiff = ((this.maxDate.valueOf() - new Date().valueOf()));
    var maxDateDiffInDays = Math.ceil(maxDateDiff / (1000 * 3600 * 24));

    if (maxDateDiffInDays > this.datePickerForwardDays) {
      let newMaxDate = new Date();
      newMaxDate.setDate(newMaxDate.getDate() + this.datePickerForwardDays);
      this.maxDate = newMaxDate;
    }

    this.minDate.setHours(0, 0, 0 ,0);
    this.maxDate.setHours(0, 0, 0 ,0);

    this.maxDateLessThanMinDate = this.maxDate < this.minDate;
  }

  private createForm(): void {
    this.applyDateLimits();

    this.frmSessionDatePicker = this.formBuilder.group({
      startDate: [null, Validators.compose([
        Validators.required,
        CustomValidators.DateRangeValidator('startDate', this.minDate, this.maxDate)
      ])],
      startTime: [null, Validators.compose([
        Validators.required,
        CustomValidators.patternValidator(/^(0[0-9]|1[0-9]|2[0-3]|[0-9]):[0-6][5]|[0-6][0]$/,
          {
            hasFiveMinsIncrements: true
          }),
        this.timeLessThan('', { StartTimeCannotBeLaterThanEndTime: true })
      ])],
      endTime: [null, Validators.compose([
        Validators.required,
        CustomValidators.patternValidator(/^(0[0-9]|1[0-9]|2[0-3]|[0-9]):[0-6][5]|[0-6][0]$/,
          {
            hasFiveMinsIncrements: true
          })
      ])],
      status: [null, null]
    });

    if (this.placementData !== null 
      && this.placementData.placementStartDate !== null) {
      this.frmSessionDatePicker.get('startDate').setValue(this.placementData.placementStartDate);
    }

  }

  private setTimeValidation(): void {
    this.frmSessionDatePicker.get("endTime").setValidators([Validators.compose([
      Validators.required,
      CustomValidators.patternValidator(/^(0[0-9]|1[0-9]|2[0-3]|[0-9]):[0-6][5]|[0-6][0]$/,
        {
          hasFiveMinsIncrements: true
        }),
      this.timeGreaterThan('', { EndTimeCannotBeEarlierThanStartTime: true }),
      this.timeGreaterThan('1', { EndTimeCannotBeEarlierThanStartTime: true }),
      this.timeGreaterThan('2', { EndTimeCannotbeGreaterThanFiveHours: true }),
      this.timeGreaterThan('3', { EndTimeCannotbeGreaterThanFiveHours: true })
    ])])

    this.frmSessionDatePicker.get("startTime").setValidators([Validators.compose([
      Validators.required,
      CustomValidators.patternValidator(/^(0[0-9]|1[0-9]|2[0-3]|[0-9]):[0-6][5]|[0-6][0]$/,
        {
          hasFiveMinsIncrements: true
        }),
      this.timeLessThan('', { StartTimeCannotBeLaterThanEndTime: true })
    ])]);
    this.frmSessionDatePicker.get("endTime").updateValueAndValidity();
    this.frmSessionDatePicker.get("startTime").updateValueAndValidity();
  }

  private timeGreaterThan(to: string, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      this.sessionTimeError = '';
      var stVal = this.frmSessionDatePicker.get('startTime').value;
      if (to === '1') {
        if (Date.parse('01/01/2010 ' + control.value) <= Date.parse('01/01/2010 ' + stVal)) {
          return error;
        }
      }

      var diff = Date.parse('01/01/2010 ' + control.value) - Date.parse('01/01/2010 ' + stVal)
      if (to === '2') {

        if ((!this.placementData.isGroup && this.tutorReportsHelperService.studentSession.sessionType !== null
          && this.tutorReportsHelperService.studentSession.sessionType !== undefined
          && this.tutorReportsHelperService.studentSession.sessionType.indexOf('Tuition') != -1)
          || (this.placementData.isGroup && this.tutorReportsHelperService.studentGroupSession.sessionType !== null
            && this.tutorReportsHelperService.studentGroupSession.sessionType !== undefined
            && this.tutorReportsHelperService.studentGroupSession.sessionType.indexOf('Online') != -1)) {
          if (diff / (1000 * 60 * 60) > 5) {
            this.sessionTimeError = 'The session cannot be greater than five hours';
            return error;
          }
        }
        else {
          if (diff / (1000 * 60 * 60) > 2) {
            this.sessionTimeError = 'The session cannot be greater than two hours';
            return error;
          }
        }
      }

      if (to == '3') {
        if (this.placementData.lockTime == true) {
          if (diff / (1000 * 60) < 10) {
            this.sessionTimeError = 'The session cannot be less than 10 minutes';
            return error;
          }
          return null;
        }

        if (diff / (1000 * 60) < 30) {
          this.sessionTimeError = 'The session cannot be less than 30 minutes';
          return error;
        }

        if (diff / (1000 * 60) < 30) {
          this.sessionTimeError = 'The session cannot be less than 30 minutes';
          return error;
        }
      }

      return null;
    }
  }

  private timeLessThan(to: string, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      var endVal = this.frmSessionDatePicker.get('endTime').value;
      if (endVal == null) {
        endVal = "00:00.00";
      }
      let startDate = new Date('01/01/2010 ' + control.value);
      let endDate = new Date('01/01/2010 ' + endVal);
      if (startDate.getTime() >= endDate.getTime()) {
        return error;
      }
      return null;
    }
  }

  private dateSessionError(er: boolean, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      if (er) {
        return error;
      }
      return null;
    }
  }

  private setDate(date): number {
    var d = new Date(date);
    return d.setHours(0, 0, 0, 0);
  }

  private populateTimesheet(data) {
    this.frmSessionDatePicker.get("startDate").setValue(data.startDate);
    this.frmSessionDatePicker.get("startTime").setValue(data.startTime);
    this.frmSessionDatePicker.get("endTime").setValue(data.endTime);
  }

  private populateForm(data): void {

    if (this.frmSessionDatePicker == null) {
      return;
    }

    this.errorText = '';
    this.frmSessionDatePicker.enable();
    if (data && data.startDate != null) {

      this.setDisplaySelectedDay(data.startDate);

      this.frmSessionDatePicker.get('startDate').setValue(data.startDate);
      this.frmSessionDatePicker.get('startTime').setValue(data.startTime);
      this.frmSessionDatePicker.get('endTime').setValue(data.endTime);

    } else {
      var startDate = this.frmSessionDatePicker.value.startDate;

      this.setDisplaySelectedDay(startDate);

      this.frmSessionDatePicker.reset();
      this.frmSessionDatePicker.get('startDate').setValue(startDate);

      if (this.tutorReportsHelperService.commonData !== null) {
        if (this.tutorReportsHelperService.commonData.startTime != null) {
          var hour = ("0" + new Date(this.tutorReportsHelperService.commonData.startTime.toString()).getHours()).slice(-2);
          var min = ("0" + new Date(this.tutorReportsHelperService.commonData.startTime.toString()).getMinutes()).slice(-2);
          this.frmSessionDatePicker.get('startTime').setValue(hour + ':' + min);

        }
        if (this.tutorReportsHelperService.commonData.endTime != null) {
          var hour = ("0" + new Date(this.tutorReportsHelperService.commonData.endTime.toString()).getHours()).slice(-2);
          var min = ("0" + new Date(this.tutorReportsHelperService.commonData.endTime.toString()).getMinutes()).slice(-2);
          this.frmSessionDatePicker.get('endTime').setValue(hour + ':' + min);
        }
      }

      this.frmSessionDatePicker.clearValidators();
      this.frmSessionDatePicker.updateValueAndValidity();
    }

    this.setTimeValidation();
    this.updateDurationDisplay();
  }

  private setDisplaySelectedDay(date: any): void {
    this.selectedDay = this.weekday[new Date(date).getDay()];
  }

  private populateTimeLockedSessionBlocks(): void {
    this.sessionService.getSessionBlocks(this.placementData.placementID, null, null).subscribe((data: Array<PlacementSession>) => {
      this.sessionBlockHolder = data;
      if(this.data.method == "Add") {

        let sliceIndex = this.sessionBlockHolder.findIndex((item: PlacementSession) => item.suggestedSession);

        if (sliceIndex != -1) {
          let sliceStart = sliceIndex + 1;
    
          if (sliceIndex < (this.showMore ? 16 : 3)) {
            this.sessionBlockPageNumber = 1
          } else {
            this.sessionBlockPageNumber = Math.floor(sliceStart/(this.showMore ? 16 : 3));
            if (sliceStart % (this.showMore ? 16 : 3) != 0) {
              this.sessionBlockPageNumber ++;
            }
          }
        }

        this.setSessionBlockDisplayArray();
      } else {
        this.displayCurrentSelectedBlock();
      }
    }, error => {
      this.errorModalHelper.show(error.message, error);
    });
  }

  private displayCurrentSelectedBlock(): void {
    let sliceIndex = this.sessionBlockHolder.findIndex((item: PlacementSession) => {
      return new Date(item.startDate).toDateString() == new Date(this.data.startDate).toDateString() && item.startTime == this.data.startTime && item.endTime == this.data.endTime;
    });
    if (sliceIndex != -1) {
      let sliceStart = sliceIndex == 0 ? sliceIndex : sliceIndex - 1;

      if (sliceStart < (this.showMore ? 16 : 3)) {
        this.sessionBlockPageNumber = 1
      } else {
        this.sessionBlockPageNumber = Math.floor(sliceStart/(this.showMore ? 16 : 3));
        if (sliceStart % (this.showMore ? 16 : 3) != 0) {
          this.sessionBlockPageNumber ++;
        }
      }

      this.sessionBlockHolder.forEach((item: PlacementSession, i: number) => {
        item.selected = false;
      });

      this.sessionBlockDisplayEndIndex = this.sessionBlockPageNumber == 1 ? (this.showMore ? 16 : 3) : this.sessionBlockPageNumber * (this.showMore ? 16 : 3);
      this.sessionBlocks = this.sessionBlockHolder.slice(this.sessionBlockDisplayEndIndex - (this.showMore ? 16 : 3), this.sessionBlockDisplayEndIndex);
      this.lastSessionBlockPage = this.sessionBlockHolder.length <= this.sessionBlockDisplayEndIndex;
      this.sessionBlocks.forEach((item: PlacementSession, i: number) => {
        if (i == sliceIndex) {
          item.selected = true;
          this.frmSessionDatePicker.get("startDate").setValue(item.startDate);
          this.frmSessionDatePicker.get("endTime").setValue(item.endTime);
          this.frmSessionDatePicker.get("startTime").setValue(item.startTime);
        } else {
          item.selected = false;
        }
      });
    } else {
      this.setSessionBlockDisplayArray();
    }
  }

}
